import React, { useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { toast } from 'react-toastify';
import { API_BASE_URL, API_URLS } from "constants/api";
import IntlStore from "stories/IntlStore";
import { FormattedMessage } from "react-intl";
import { SmartForm } from "ui-kit/SmartForm";
import { Button } from "ui-kit/Button";
import { hasValidEmail, isShowInputError, structuredValidator } from "utils/validation";
import { CheckYourEmail } from "./CheckYourEmail";
import { Layout } from "../Layout";
import { Title } from "../components/Title";
import { Input } from "../components/Input";
import { Text } from "../components/Text";
import { Step } from "./constants";

interface FormValues {
  email: string;
}

export const RecoveryPassword: React.FC = () => {
  const [step, setStep] = useState(Step.Init);
  const [email, setEmail] = useState('');

  const onSubmit = async (values: FormValues) => {
    try {
      const a = await axios.post(`${API_BASE_URL}${API_URLS.ForgotPassword}`, { email: values.email });
      setEmail(values.email);
      setStep(Step.CheckEmail);
    } catch (e: any) {
      const message = e.response.data.message;
      console.error("Error", message);
      toast.error(message);
    }
  };

  const onResend = async () => {
    try {
      await axios.post(`${API_BASE_URL}${API_URLS.ForgotPassword}`, { email });
    } catch (e: any) {
      const message = e.response.data.message;
      console.error("Error", message);
      toast.error(message);
      throw e;
    }
  };

  const onBack = () => setStep(Step.Init);

  if (step === Step.CheckEmail) {
    return (
      <Layout>
        <Container>
          <CheckYourEmail onBack={onBack} onResend={onResend} />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <SmartForm<FormValues>
        onSubmit={onSubmit}
        initialValues={{ email: '' }}
        validate={structuredValidator({ email: hasValidEmail })}
      >
        {props => {
          const { handleSubmit, errors, submitting } = props;
          return (
            <Container id="recovery-password" onSubmit={handleSubmit}>
              <Title>
                <FormattedMessage id="Auth.RecoveryPassword.Title" defaultMessage="Password reset"/>
              </Title>
              <Subtitle>
                {IntlStore.intl.formatMessage({
                  id: "Auth.RecoveryPassword.Subtitle",
                  defaultMessage: "Enter the email address linked to your Slash Music account, and we will send you a message"
                })}
              </Subtitle>
              <div>
                <Input
                  name="email"
                  placeholder={IntlStore.intl.formatMessage({ id: "Auth.EmailPlaceholder", defaultMessage: "Email" })}
                  error={isShowInputError<FormValues>(props, 'email') && <FormattedMessage id={errors?.email} />}
                />
              </div>
              <Button type="submit" form="recovery-password" loading={submitting}>
                <FormattedMessage id="Auth.RecoveryPassword.Button" defaultMessage="Get the link"/>
              </Button>
            </Container>
          );
        }}
      </SmartForm>
    </Layout>
  );
};

const Container = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 17px;
  padding: 54px;
  height: 100%;
  max-width: 548px;

  @media (max-width: 1200px) {
    max-width: 100%;
  }
`;

const Subtitle = styled(Text)`
  margin-top: -10px;
  font-size: 15px;
  line-height: 25px;
`;
